import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';

/**
 * ConfirmationDialog Component
 * Professional confirmation dialog for destructive/important actions
 *
 * Usage:
 * <ConfirmationDialog
 *   open={open}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 *   title="Delete Prayer?"
 *   message="Are you sure you want to delete this prayer? This action cannot be undone."
 *   confirmText="Delete"
 *   severity="error"
 * />
 */

const ConfirmationDialog = ({
  open = false,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning', // 'error', 'warning', 'info'
  loading = false,
  confirmColor,
}) => {
  const severityConfig = {
    error: {
      icon: DeleteIcon,
      color: 'error',
      iconColor: 'error.main',
    },
    warning: {
      icon: WarningIcon,
      color: 'warning',
      iconColor: 'warning.main',
    },
    info: {
      icon: InfoIcon,
      color: 'primary',
      iconColor: 'info.main',
    },
    danger: {
      icon: ErrorOutlineIcon,
      color: 'error',
      iconColor: 'error.main',
    },
  };

  const config = severityConfig[severity] || severityConfig.warning;
  const Icon = config.icon;
  const buttonColor = confirmColor || config.color;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirmation-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Icon sx={{ fontSize: 28, color: config.iconColor }} />
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="confirmation-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={buttonColor}
          disabled={loading}
          autoFocus
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== PRE-BUILT CONFIRMATION DIALOGS ====================

/**
 * Delete Prayer Confirmation
 */
export const DeletePrayerDialog = ({ open, onClose, onConfirm, loading }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Delete Prayer?"
    message="Are you sure you want to delete this prayer? This action cannot be undone and the prayer will be permanently removed."
    confirmText="Delete"
    severity="error"
  />
);

/**
 * Logout Confirmation
 */
export const LogoutDialog = ({ open, onClose, onConfirm, loading }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Logout?"
    message="Are you sure you want to logout? You'll need to sign in again to access your account."
    confirmText="Logout"
    severity="warning"
  />
);

/**
 * Delete Account Confirmation
 */
export const DeleteAccountDialog = ({ open, onClose, onConfirm, loading }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Delete Account?"
    message="⚠️ WARNING: This will permanently delete your account and all your prayers. This action cannot be undone. Are you absolutely sure?"
    confirmText="Delete Account"
    severity="error"
  />
);

/**
 * Discard Changes Confirmation
 */
export const DiscardChangesDialog = ({ open, onClose, onConfirm, loading }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Discard Changes?"
    message="You have unsaved changes. Are you sure you want to discard them?"
    confirmText="Discard"
    cancelText="Keep Editing"
    severity="warning"
  />
);

/**
 * Remove Prayer Collection Confirmation
 */
export const RemoveCollectionDialog = ({ open, onClose, onConfirm, loading }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Remove from Collection?"
    message="Are you sure you want to remove this prayer from your collection?"
    confirmText="Remove"
    severity="warning"
  />
);

/**
 * Clear All Notifications Confirmation
 */
export const ClearNotificationsDialog = ({ open, onClose, onConfirm, loading }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Clear All Notifications?"
    message="This will mark all notifications as read. You can still access them in your history."
    confirmText="Clear All"
    severity="info"
  />
);

/**
 * Leave Circle Confirmation
 */
export const LeaveCircleDialog = ({ open, onClose, onConfirm, loading, circleName }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Leave Circle?"
    message={`Are you sure you want to leave ${circleName || 'this circle'}? You'll need to rejoin to see the prayers again.`}
    confirmText="Leave"
    severity="warning"
  />
);

/**
 * Delete Circle Confirmation
 */
export const DeleteCircleDialog = ({ open, onClose, onConfirm, loading, circleName }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Delete Circle?"
    message={`Are you sure you want to delete ${circleName || 'this circle'}? All prayers in the circle will be permanently removed. This action cannot be undone.`}
    confirmText="Delete Circle"
    severity="error"
  />
);

/**
 * Delete Message Confirmation
 */
export const DeleteMessageDialog = ({ open, onClose, onConfirm, loading }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    loading={loading}
    title="Delete Message?"
    message="This message will be removed from the conversation. This action cannot be undone."
    confirmText="Delete"
    severity="error"
  />
);

export default ConfirmationDialog;
